import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live sensor readout — a diagnostic page, not part of the product.
 *
 * Four attempts at the driver-camera rotation bug have missed, the last one
 * because gravity goes blind when the phone is flat: spinning a flat phone
 * does not move the gravity vector at all. The owner's repro proves it —
 * tilt flat, turn to landscape, shoot (wrong), then tilt up past ~30 degrees
 * and it suddenly corrects, which is exactly the 24-degree gravity threshold
 * in camera.js being crossed.
 *
 * The gyroscope CAN see that flat turn (it is a rotation about the screen's
 * own axis) but its sign convention differs between devices, and guessing it
 * is how attempts one and two went wrong. So this page shows both candidate
 * answers side by side and lets the phone that actually fails tell us which
 * one is right, before a line of camera code is written.
 *
 * No login, no data, no writes — just sensors.
 */

const GRAVITY_TRUST = 6;          // m/s^2 in-plane, ~37 deg of tilt
const STEADY_MS = 250;
const RATE_DEADZONE = 2;          // deg/s — below this is sensor bias
const QUARTER = (deg) => ((Math.round(deg / 90) * 90) % 360 + 360) % 360;

export default function SensorCheckPage() {
  const [granted, setGranted] = useState(false);
  const [err, setErr] = useState(null);
  const [frozen, setFrozen] = useState(false);
  const [live, setLive] = useState(null);
  const [log, setLog] = useState([]);

  const spin = useRef(0);           // integrated gyro rotation about the screen axis
  const anchor = useRef(0);         // last quarter gravity was confident about
  const lastTs = useRef(0);
  const frozenRef = useRef(false);
  useEffect(() => { frozenRef.current = frozen; }, [frozen]);

  const start = useCallback(async () => {
    setErr(null);
    try {
      const Motion = window.DeviceMotionEvent;
      if (!Motion) { setErr("This phone exposes no motion sensors to the browser."); return; }
      if (Motion.requestPermission) {
        const res = await Motion.requestPermission();
        if (res !== "granted") { setErr("Motion access was declined. Tap again and allow it."); return; }
      }
      setGranted(true);
    } catch (e) {
      setErr(`Could not start sensors: ${e?.message || e}`);
    }
  }, []);

  useEffect(() => {
    if (!granted) return undefined;

    const onMotion = (event) => {
      if (frozenRef.current) return;
      const g = event.accelerationIncludingGravity;
      const rate = event.rotationRate;
      if (!g || g.x == null) return;

      const now = event.timeStamp || performance.now();
      const dt = lastTs.current ? Math.min((now - lastTs.current) / 1000, 0.25) : 0;
      lastTs.current = now;

      const inPlane = Math.hypot(g.x, g.y);
      const tiltFromFlat = Math.round((Math.atan2(inPlane, Math.abs(g.z ?? 0)) * 180) / Math.PI);
      const trusted = inPlane >= GRAVITY_TRUST;

      // Integrate always, with a deadzone so bias cannot drift while flat.
      if (dt && rate?.alpha != null && Math.abs(rate.alpha) > RATE_DEADZONE) {
        spin.current += rate.alpha * dt;
      }

      const gravityQuarter = QUARTER((Math.atan2(-g.x, g.y) * 180) / Math.PI);
      if (trusted) {
        if (gravityQuarter === steadyQuarter.current) {
          if (now - steadySince.current >= STEADY_MS) { anchor.current = gravityQuarter; spin.current = 0; }
        } else {
          steadyQuarter.current = gravityQuarter;
          steadySince.current = now;
        }
      } else {
        steadyQuarter.current = null;
      }

      const physical = QUARTER(anchor.current + spin.current);
      const screenAngle = Number(window.screen?.orientation?.angle ?? window.orientation ?? 0) || 0;

      setLive({
        gx: g.x, gy: g.y, gz: g.z,
        inPlane, tiltFromFlat, confident: trusted,
        alphaRate: rate?.alpha ?? null,
        spin: spin.current,
        anchor: anchor.current,
        gravityQuarter,
        physical,
        screenAngle,
        screenType: window.screen?.orientation?.type || "n/a",
        shipped: QUARTER(physical + screenAngle),
      });
    };

    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
  }, [granted]);

  const mark = (label) => {
    if (!live) return;
    setLog((rows) => [
      {
        label,
        at: new Date().toLocaleTimeString(),
        tilt: live.tiltFromFlat,
        inPlane: Number(live.inPlane.toFixed(2)),
        confident: live.confident,
        gravity: live.gravityQuarter,
        anchor: live.anchor,
        spin: Number(live.spin.toFixed(1)),
        physical: live.physical,
        screen: live.screenAngle,
        shipped: live.shipped,
      },
      ...rows,
    ].slice(0, 12));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ ua: navigator.userAgent, rows: log }, null, 1));
      alert("Readings copied — paste them into the chat.");
    } catch {
      alert("Copy failed. Screenshot this page instead.");
    }
  };

  const Row = ({ k, v, hint }) => (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-800 py-1.5">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{k}</span>
      <span className="text-right">
        <span className="font-mono text-base text-slate-50">{v}</span>
        {hint && <span className="block text-[10px] text-slate-500">{hint}</span>}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-5 text-slate-100" data-testid="sensor-check-page">
      <h1 className="text-xl font-bold">Camera sensor check</h1>
      <p className="mt-1 text-xs text-slate-400">
        Diagnostic only. Nothing is saved and no photo is taken.
      </p>

      {!granted ? (
        <div className="mt-6">
          <button
            type="button"
            onClick={start}
            className="w-full rounded-xl bg-sky-500 px-4 py-4 text-base font-bold text-slate-950"
            data-testid="sensor-check-start"
          >
            Start sensors
          </button>
          {err && <p className="mt-3 text-sm text-rose-400" data-testid="sensor-check-error">{err}</p>}
        </div>
      ) : (
        <>
          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
            <p className="mb-2 text-[11px] uppercase tracking-wide text-sky-400">
              What a photo would come out as, right now
            </p>
            <div className="flex items-center justify-between">
              <span className="font-mono text-3xl font-bold" data-testid="sensor-check-verdict">
                {live ? (live.shipped === 90 || live.shipped === 270 ? "LANDSCAPE" : "PORTRAIT") : "—"}
              </span>
              <span className="font-mono text-xl text-slate-400">
                {live ? `${live.shipped}°` : ""}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              Gravity where it can be trusted, gyro across the flat patch.
            </p>
          </div>

          <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
            <p className="mb-1 text-[11px] uppercase tracking-wide text-amber-400">
              How it worked that out
            </p>
            <div className="flex justify-between gap-4">
              <div>
                <span className="block text-[10px] text-slate-500">gravity anchor</span>
                <span className="font-mono text-2xl" data-testid="sensor-check-anchor">{live ? `${live.anchor}°` : "—"}</span>
              </div>
              <div className="text-center">
                <span className="block text-[10px] text-slate-500">+ gyro spin</span>
                <span className="font-mono text-2xl" data-testid="sensor-check-spin">{live ? `${live.spin.toFixed(0)}°` : "—"}</span>
              </div>
              <div className="text-right">
                <span className="block text-[10px] text-slate-500">= phone is at</span>
                <span className="font-mono text-2xl" data-testid="sensor-check-physical">{live ? `${live.physical}°` : "—"}</span>
              </div>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              Turned flat to landscape, "phone is at" should reach 90° or 270° even
              though gravity is blind.
            </p>
          </div>

          <div className="mt-4 space-y-0">
            <Row k="Tilt off flat" v={live ? `${live.tiltFromFlat}°` : "—"} hint="0° = flat on its back, 90° = held upright" />
            <Row
              k="Gravity usable"
              v={live ? (live.confident ? "YES" : "NO — blind") : "—"}
              hint={live ? `in-plane ${live.inPlane.toFixed(2)} of ${GRAVITY_FLOOR} needed` : ""}
            />
            <Row k="Gravity says" v={live ? `${live.gravityQuarter}°` : "—"} />
            <Row k="Gyro spin since anchor" v={live ? `${live.spin.toFixed(0)}°` : "—"} hint="should reach ~90 when turned flat" />
            <Row k="Spin rate (alpha)" v={live ? `${(live.alphaRate ?? 0).toFixed(1)}/s` : "—"} />
            <Row k="Screen angle" v={live ? `${live.screenAngle}° ${live.screenType}` : "—"} hint="stays 0 when auto-rotate fails" />
            <Row k="gx / gy / gz" v={live ? `${live.gx?.toFixed(1)} / ${live.gy?.toFixed(1)} / ${live.gz?.toFixed(1)}` : "—"} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => mark("portrait upright")}
              className="rounded-lg bg-slate-800 px-3 py-3 text-xs font-bold"
              data-testid="sensor-check-mark-portrait"
            >
              Mark: portrait upright
            </button>
            <button
              type="button"
              onClick={() => mark("flat + turned landscape")}
              className="rounded-lg bg-amber-500 px-3 py-3 text-xs font-bold text-slate-950"
              data-testid="sensor-check-mark-flat"
            >
              Mark: flat + landscape
            </button>
            <button
              type="button"
              onClick={() => mark("tilted up, landscape")}
              className="rounded-lg bg-slate-800 px-3 py-3 text-xs font-bold"
              data-testid="sensor-check-mark-tilted"
            >
              Mark: tilted up, landscape
            </button>
            <button
              type="button"
              onClick={() => setFrozen((f) => !f)}
              className="rounded-lg bg-slate-800 px-3 py-3 text-xs font-bold"
              data-testid="sensor-check-freeze"
            >
              {frozen ? "Resume" : "Freeze"}
            </button>
          </div>

          {log.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Marked readings</p>
                <button
                  type="button"
                  onClick={copy}
                  className="rounded bg-sky-500 px-3 py-1.5 text-[11px] font-bold text-slate-950"
                  data-testid="sensor-check-copy"
                >
                  Copy all
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {log.map((r, i) => (
                  <pre
                    key={`${r.at}-${i}`}
                    className="overflow-x-auto rounded bg-slate-900 p-2 text-[10px] leading-relaxed text-slate-300"
                  >
{`${r.label} @ ${r.at}
 tilt ${r.tilt}°  in-plane ${r.inPlane}  gravity usable: ${r.confident}
 gravity ${r.gravity}°  anchor ${r.anchor}°  spin ${r.spin}°
 phone is at ${r.physical}°  screen ${r.screen}°
 -> camera decides ${r.shipped}°`}
                  </pre>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
            <p className="font-bold text-slate-200">Your repro, step by step</p>
            <ol className="mt-1 list-decimal space-y-1 pl-4">
              <li>Hold it upright in portrait — tap <b>Mark: portrait upright</b></li>
              <li>Tilt it flat, facing the ground, still portrait</li>
              <li>Turn it 90° to landscape while flat — tap <b>Mark: flat + landscape</b></li>
              <li>Slowly tilt up to about 45° — tap <b>Mark: tilted up, landscape</b></li>
              <li>Tap <b>Copy all</b> and paste it into the chat</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
